import {
  ChevronRightIcon,
  PhoneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';

import {
  BPOAction,
  SUCCESS_STATUS_DISPLAY,
  VoiceAIAgentRunSchema,
} from '@/types/enhanced-bland-call';

import { CallDetailCard, CallDisplayEntry } from '../call-list/CallDetailCard';
import { CallEvaluationBadge } from '../call-list/CallEvaluationBadge';
import { formatDate, formatDuration, getStatusEmoji } from '../utils';
import { VoiceAIRunTimeline } from '../VoiceAIRunTimeline';

const formatEventTimestamp = (
  timestamp: string,
): { time: string; date: string } => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  let time: string;
  if (diffMinutes < 1) {
    time = 'Just now';
  } else if (diffMinutes < 60) {
    time = `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    time = `${diffHours}h ago`;
  } else if (diffDays < 7) {
    time = `${diffDays}d ago`;
  } else {
    time = date.toLocaleDateString();
  }

  const dateStr =
    date.toLocaleDateString() === now.toLocaleDateString()
      ? 'Today'
      : date.toLocaleDateString();

  return { time, date: dateStr };
};

const TERMINAL_BPO_TYPES = new Set([
  'completed',
  'failed',
  'cancelled',
  'expired',
]);

const getActionTimestamp = (action?: BPOAction): string | null => {
  if (!action) return null;
  return (
    action.received_at ||
    action.requested_at ||
    action.sla_due_at ||
    action.expired_at ||
    action.cancelled_at ||
    null
  );
};

const getMetadata = (action?: BPOAction): Record<string, unknown> => {
  if (!action || !action.metadata || typeof action.metadata !== 'object') {
    return {};
  }
  return action.metadata as Record<string, unknown>;
};

const buildVoiceCallEntries = (
  run: VoiceAIAgentRunSchema,
): CallDisplayEntry[] => {
  return (run.call_results || []).map((call) => {
    const requestedAt =
      call.started_at || call.created_at || run.created_at || undefined;
    const completedAt =
      call.created_at || call.started_at || run.updated_at || undefined;

    return {
      id: call.call_id || call.id,
      type: 'voice_ai',
      phoneNumber: call.phone_number,
      requestedAt,
      completedAt,
      durationSeconds: call.duration_seconds ?? call.duration ?? undefined,
      statusLabel: call.status ? call.status.replace(/_/g, ' ') : undefined,
      evaluation: call.evaluation
        ? {
            label:
              SUCCESS_STATUS_DISPLAY[call.evaluation.success_status]?.label ||
              'Evaluation',
            summary: call.evaluation.call_summary,
            score: call.evaluation.score ?? null,
            confidence: call.evaluation.confidence ?? null,
            recordedAt: completedAt || call.created_at || undefined,
            source: 'ai',
            successStatus: call.evaluation.success_status,
          }
        : undefined,
    };
  });
};

export const buildBpoCallEntries = (
  actions?: BPOAction[] | null,
): CallDisplayEntry[] => {
  if (!Array.isArray(actions) || actions.length === 0) return [];

  const grouped = actions.reduce<Map<string, BPOAction[]>>(
    (acc, action, index) => {
      const meta = getMetadata(action);
      const callId =
        action.ringcentral_call_id ||
        action.dialpad_call_id ||
        (meta.ringcentral_call_id as string | undefined) ||
        (meta.dialpad_call_id as string | undefined) ||
        (meta.call_id as string | undefined) ||
        `bpo-${index}`;
      const existing = acc.get(callId) || [];
      existing.push(action);
      acc.set(callId, existing);
      return acc;
    },
    new Map(),
  );

  return Array.from(grouped.entries()).map(([callId, callActions], index) => {
    const chronological = [...callActions].sort(
      (a, b) =>
        new Date(getActionTimestamp(a) || 0).getTime() -
        new Date(getActionTimestamp(b) || 0).getTime(),
    );

    const started =
      chronological.find((action) => action.type === 'call_started') ??
      chronological[0];
    const terminal = chronological.filter((action) =>
      TERMINAL_BPO_TYPES.has(action.type),
    );

    const humanEval = [...terminal].reverse().find((action) => {
      const generatedBy = String(
        getMetadata(action).generated_by || '',
      ).toLowerCase();
      return generatedBy === 'human';
    });

    const evaluationAction =
      humanEval ||
      [...terminal].sort(
        (a, b) =>
          new Date(getActionTimestamp(b) || 0).getTime() -
          new Date(getActionTimestamp(a) || 0).getTime(),
      )[0] ||
      null;

    const evaluationMeta = getMetadata(evaluationAction || undefined);
    const startMeta = getMetadata(started);
    const requestedAt = getActionTimestamp(started) || undefined;
    const completedAt =
      getActionTimestamp(evaluationAction ?? terminal[terminal.length - 1]) ||
      requestedAt ||
      undefined;
    const sourceRaw = String(evaluationMeta.generated_by || '').toLowerCase();
    const evaluationSource: 'human' | 'ai' | 'unknown' =
      sourceRaw === 'human'
        ? 'human'
        : sourceRaw === 'agent'
          ? 'ai'
          : 'unknown';
    const outcome =
      typeof evaluationMeta.call_outcome === 'string'
        ? (evaluationMeta.call_outcome as string)
        : null;
    const summary =
      typeof evaluationMeta.evaluation_summary === 'string'
        ? (evaluationMeta.evaluation_summary as string)
        : typeof evaluationMeta.call_issues === 'string'
          ? (evaluationMeta.call_issues as string)
          : null;
    const score =
      typeof evaluationMeta.evaluation_score === 'number'
        ? (evaluationMeta.evaluation_score as number)
        : null;
    const confidence =
      typeof evaluationMeta.evaluation_confidence === 'number'
        ? (evaluationMeta.evaluation_confidence as number)
        : null;
    const successBoolean =
      typeof evaluationMeta.call_success === 'boolean'
        ? (evaluationMeta.call_success as boolean)
        : evaluationAction
          ? evaluationAction.type === 'completed'
            ? true
            : evaluationAction.type === 'failed'
              ? false
              : null
          : null;
    const callIssues =
      typeof evaluationMeta.call_issues === 'string'
        ? (evaluationMeta.call_issues as string)
        : null;
    const escalationNotes =
      typeof evaluationMeta.escalation_notes === 'string'
        ? (evaluationMeta.escalation_notes as string)
        : null;
    const notes = callIssues || escalationNotes || null;

    const phoneNumber =
      (startMeta.to_number as string | undefined) ||
      (evaluationMeta.to_number as string | undefined) ||
      (evaluationMeta.phone_number as string | undefined) ||
      null;
    const fromNumber =
      (startMeta.from_number as string | undefined) ||
      (evaluationMeta.from_number as string | undefined) ||
      undefined;

    const detailRows: Array<{ label: string; value: string }> = [];
    const candidateDetails: Array<[string, unknown]> = [
      ['claim number', evaluationMeta.claim_number],
      ['policy number', evaluationMeta.policy_number],
      ['adjuster name', evaluationMeta.adjuster_name],
      ['adjuster phone', evaluationMeta.adjuster_phone],
      ['adjuster email', evaluationMeta.adjuster_email],
      ['adjuster fax', evaluationMeta.adjuster_fax],
      ['adjuster contact', evaluationMeta.adjuster_contact],
    ];
    candidateDetails.forEach(([label, value]) => {
      if (typeof value === 'string' && value.trim().length > 0) {
        detailRows.push({ label, value: value.trim() });
      }
    });

    return {
      id: `${callId}-${index}`,
      type: 'bpo' as const,
      sourceCallId: callId,
      phoneNumber,
      callerNumber: fromNumber || null,
      requestedAt,
      completedAt,
      statusLabel:
        outcome ||
        (evaluationAction?.type
          ? evaluationAction.type.replace(/_/g, ' ')
          : 'BPO call'),
      evaluation: evaluationAction
        ? {
            label:
              outcome ||
              (evaluationAction.type
                ? evaluationAction.type.replace(/_/g, ' ')
                : 'Outcome'),
            summary,
            score,
            confidence,
            recordedAt: getActionTimestamp(evaluationAction) || undefined,
            source: evaluationSource,
            successBoolean,
          }
        : undefined,
      detailRows: successBoolean ? detailRows : undefined,
      notes,
    };
  });
};

const getSortValue = (entry: CallDisplayEntry): number => {
  const timestamp = entry.requestedAt || entry.completedAt;
  if (!timestamp) return 0;
  const value = new Date(timestamp).getTime();
  return Number.isNaN(value) ? 0 : value;
};

const buildCallEntries = (run: VoiceAIAgentRunSchema): CallDisplayEntry[] => {
  const entries = [
    ...buildVoiceCallEntries(run),
    ...buildBpoCallEntries(run.bpo_actions),
  ];

  return entries.sort((a, b) => getSortValue(b) - getSortValue(a));
};

export const RunDetailView: React.FC<{
  run: VoiceAIAgentRunSchema;
  onBack?: () => void;
  isCurrentRun?: boolean;
  /** Use new timeline view (default: true) */
  useTimeline?: boolean;
}> = ({ run, onBack, isCurrentRun = false, useTimeline = true }) => {
  const [isEventLogOpen, setIsEventLogOpen] = useState(false);
  const callEntries = useMemo(() => buildCallEntries(run), [run]);
  const totalCalls = callEntries.length;
  const voiceCallCount = run.call_results?.length ?? 0;
  const bpoCallCount = callEntries.filter(
    (entry) => entry.type === 'bpo',
  ).length;

  return (
    <div className="space-y-6">
      {!isCurrentRun && (
        <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRightIcon className="h-4 w-4 rotate-180 text-gray-500" />
            </button>
          )}
          <div>
            <h3 className="font-semibold text-gray-900">Run details</h3>
            <p className="text-sm text-gray-600">
              Started {formatDate(run.created_at)}
            </p>
          </div>
        </div>
      )}

      {/* New Timeline View */}
      {useTimeline && <VoiceAIRunTimeline run={run} showCollectedData />}

      {/* Legacy detailed view (shown below timeline or standalone) */}
      {!useTimeline && (
        <>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase text-gray-500 tracking-wide">
                Voice AI calls made
              </div>
              <div className="text-2xl font-semibold text-gray-900">
                {voiceCallCount}/{run.max_calls_allowed}
              </div>
              {bpoCallCount > 0 && (
                <div className="text-xs text-gray-500">
                  {bpoCallCount} BPO {bpoCallCount === 1 ? 'call' : 'calls'}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsEventLogOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-white"
            >
              View event log
            </button>
          </div>

          {run.summary && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-1">Run summary</h4>
              <p className="text-sm text-gray-700">{run.summary}</p>
            </div>
          )}

          <div>
            <h4 className="font-medium text-gray-900 mb-4">Call details</h4>
            {totalCalls > 0 ? (
              <div className="space-y-4">
                {callEntries.map((entry, index) => (
                  <CallDetailCard
                    key={`${entry.id}-${index}`}
                    entry={entry}
                    callNumber={totalCalls - index}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                No call attempts yet.
              </div>
            )}
          </div>
        </>
      )}

      {/* Event log button (always available) */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setIsEventLogOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
        >
          View raw event log
        </button>
      </div>

      {isEventLogOpen && (
        <EventLogModal run={run} onClose={() => setIsEventLogOpen(false)} />
      )}
    </div>
  );
};

const EventLogModal: React.FC<{
  run: VoiceAIAgentRunSchema;
  onClose: () => void;
}> = ({ run, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
    <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h3 className="text-base font-semibold text-gray-900">
          Workflow event log
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-full hover:bg-gray-100"
          aria-label="Close event log"
        >
          <XMarkIcon className="h-5 w-5 text-gray-500" />
        </button>
      </div>
      <div className="p-4 space-y-6 overflow-y-auto max-h-[80vh]">
        <section>
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            Voice AI events
          </h4>
          {run.event_log && run.event_log.length > 0 ? (
            <div className="space-y-3">
              {run.event_log
                .slice()
                .reverse()
                .map((event, idx) => {
                  const { time, date } = formatEventTimestamp(event.timestamp);
                  return (
                    <div
                      key={`${event.timestamp}-${idx}`}
                      className="flex gap-3 text-sm"
                    >
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="font-medium text-gray-900">
                            {event.title}
                          </div>
                          <div className="text-xs text-gray-500 flex flex-col items-end">
                            <span>{time}</span>
                            {date !== 'Today' && (
                              <span className="text-gray-400">{date}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-gray-600 break-words">
                          {event.description}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No event log entries yet.</p>
          )}
        </section>
        <section>
          <h4 className="text-sm font-medium text-gray-900 mb-2">
            BPO actions
          </h4>
          {run.bpo_actions && run.bpo_actions.length > 0 ? (
            <pre className="text-xs bg-gray-900 text-gray-100 rounded-lg p-3 overflow-x-auto">
              {JSON.stringify(run.bpo_actions, null, 2)}
            </pre>
          ) : (
            <p className="text-sm text-gray-500">No BPO activity recorded.</p>
          )}
        </section>
      </div>
    </div>
  </div>
);

export const RunHistoryList: React.FC<{
  runs: VoiceAIAgentRunSchema[];
  onSelectRun: (run: VoiceAIAgentRunSchema) => void;
}> = ({ runs, onSelectRun }) => (
  <div className="space-y-4">
    {runs.map((run) => (
      <button
        key={run.id}
        type="button"
        onClick={() => onSelectRun(run)}
        className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm hover:border-gray-300 transition-all"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl">{getStatusEmoji(run.status)}</span>
            <div>
              <div className="font-medium text-gray-900">
                {run.status
                  .replace('_', ' ')
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </div>
              <div className="text-sm text-gray-600">
                {run.call_results?.length || 0} calls •{' '}
                {formatDate(run.created_at, { fromNow: true })}
              </div>
            </div>
          </div>
          <ChevronRightIcon className="h-4 w-4 text-gray-400" />
        </div>

        {(run.summary || run.error_message) && (
          <div className="mt-3 space-y-2">
            {run.summary && (
              <div className="text-sm text-gray-700 bg-gray-50 rounded p-2">
                {run.summary}
              </div>
            )}
            {run.error_message && (
              <div className="text-sm text-red-600 bg-red-50 rounded p-2">
                {run.error_message}
              </div>
            )}
          </div>
        )}

        {run.call_results && run.call_results.length > 0 && (
          <div className="space-y-2 mt-3">
            {run.call_results.map((call) => (
              <div
                key={call.id}
                className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded"
              >
                <div className="flex items-center gap-2">
                  <PhoneIcon className="h-3 w-3 text-gray-400" />
                  <span className="text-sm text-gray-700">
                    {call.phone_number}
                  </span>
                  {call.evaluation && (
                    <CallEvaluationBadge
                      successStatus={call.evaluation.success_status}
                      size="sm"
                    />
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {formatDuration(call.duration_seconds)}
                </div>
              </div>
            ))}
          </div>
        )}
      </button>
    ))}
  </div>
);
